import { supabase } from "@/lib/supabase";
import BaseService from "@/services/BaseService.ts";
import {
  CreateReitDividendPaymentDto,
  UpdateReitHoldingDto,
  CreateReitDepositDto,
  CreateReitDividendDto,
  RecordREITDividendDto,
  ReitTypeResponse,
  ReitSectorResponse
} from "@/@types/reit.dto";
import {ReitDeposit, ReitHolding, ReitDividendPayment} from "@/@types/reit.models.ts";
import {CreateReitHoldingDto} from "@/@types/reit.dto";
import {convertToAccountCurrency} from "@lib/currencyConversion/accountCurrency.ts";

/**
 * Service class for handling REIT-related operations, including fetching dividend payments.
 * This class provides methods to interact with the Supabase backend for REIT data.
 *
 * @class ReitService
 * @example
 * const reitService = new ReitService();
 * const payments = await reitService.getReitDividendPayments(userId);
 */
export class ReitService extends BaseService {
  private readonly reitDividedPaymentsPath: string = "reit_dividend_payments";
  private readonly reitHoldingsPath: string = "reit_holdings";
  private readonly reitDepositsPath: string = "reit_deposits";

  // REIT holdings
  /**
   * Create holding for the user
   * @param {string} userId the user's ID
   * @param {CreateReitHoldingDto} request request to send
   */
  public async createHolding(
    userId: string,
    request: CreateReitHoldingDto,
  ): Promise<ReitHolding> {
    try {
      const {
        reit_name,
        reit_type,
        ticker_symbol,
        sector,
        units_held,
        purchase_price_per_unit,
        current_price_per_unit,
        currency,
        purchase_date,
        dividend_per_unit,
        dividend_frequency,
        nav_per_unit,
        occupancy_rate,
        notes
      } = request
      const { data, error } = await supabase
          .from("reit_holdings")
          .insert({
            user_id: userId,
            reit_name,
            ticker_symbol,
            reit_type,
            sector,
            units_held,
            purchase_price_per_unit,
            current_price_per_unit,
            currency,
            purchase_date,
            dividend_per_unit,
            dividend_frequency,
            nav_per_unit,
            occupancy_rate,
            notes,
          })
          .select()
          .single();
      if (error) throw error;
      return data;
    } catch (error) {
      console.error("Error creating REIT holding:", error);
      await this.handleError(error);
    }
  }

  /**
   * Updates a REIT Holding
   * @param {UpdateReitHoldingDto} request update request
   */
  public async updateHolding(
    request: UpdateReitHoldingDto,
  ): Promise<ReitHolding> {
    try {
      const payload = this.convertRequestToSnakeCase(request);
      const { data, error } = await supabase
          .from('reit_holdings')
          .update(payload)
          .eq('id', request.id)
          .select()
          .single();
      if (error) throw error;
      return data;
    } catch (error) {
      console.error("Error updating REIT holding:", error);
      await this.handleError(error);
    }
  }

  /**
   * Deletes a REIT Holding
   * @param {string} id REIT holding ID
   */
  public async deleteHolding(
    id: string,
  ): Promise<void> {
    try {
      const { error } = await supabase
          .from('reit_holdings')
          .delete()
          .eq('id', id);
      if (error) throw error;
    } catch (error) {
      console.error("Error deleting REIT holding:", error);
      await this.handleError(error);
    }
  }

  public async getHoldings(
    userId: string,
  ): Promise<ReitHolding[]> {
    try {
      const { data, error } = await supabase
        .from("reit_holdings")
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

        if (error) throw error;

        return data
    } catch (error) {
      console.error("Error fetching REIT dividend payments:", error);
      await this.handleError(error);
    }
  }

  // Dividend Payment operations

  /**
   * Fetches all REIT dividend payments for a specific user from the backend.
   * The payments are ordered by payment date in descending order.
   * @param userId {string} - The ID of the user for whom to fetch REIT dividend payments.
   * @param holdingId {string} [optional] - The ID of the specific REIT holding to filter payments by. If not provided, all payments for the user will be fetched.
   * @throws Will throw an error if the Supabase query fails.
   * @returns
   */
  public async getDividendPayments(
    userId: string,
    holdingId?: string
  ): Promise<ReitDividendPayment[]> {
    try {
    let query = supabase
        .from("reit_dividend_payments")
        .select('*')
        .eq('user_id', userId)
        .order('payment_date', { ascending: false });

    if (holdingId) query = query.eq('reit_holding_id', holdingId);
      
      const { data, error } = await query;
      if (error) throw error;
      return data;
    } catch (error) {
      console.error("Error fetching REIT dividend payments:", error);
      await this.handleError(error);
    }
  }

  /**
   * Creates a new dividend payment record in the database for the specified user.
   *
   * @param {string} userId - The unique identifier of the user for whom the dividend payment is being created.
   * @param {CreateReitDividendPaymentDto} request - The data transfer object containing the details of the dividend payment.
   * @return {Promise<ReitDividendPayment[]>} A promise resolving to an array of created REIT dividend payment records.
   */
  public async createDividendPayment(
    userId: string,
    request: CreateReitDividendPaymentDto
  ): Promise<ReitDividendPayment> {
    try {
      const payload = this.convertRequestToSnakeCase(request)
      const { data, error } = await supabase
          .from('reit_dividend_payments')
          .insert({ ...payload, user_id: userId })
          .select()
          .single();
      if (error) throw error;
      return data;
    } catch (error) {
      console.error("Error creating REIT dividend payment:", error);
      await this.handleError(error);
    }
  }

  // Reit Deposit operations

  /**
   * Retrieves a list of REIT deposits for a specific user. Optionally filters the deposits by a specific holding.
   *
   * @param {string} userId - The unique identifier of the user whose deposits are being retrieved.
   * @param {string} [holdingId] - Optional. The unique identifier of the REIT holding for filtering deposits.
   * @return {Promise<ReitDeposit>} A promise that resolves to a list of REIT deposit records.
   */
  public async getDeposits(userId: string, holdingId?: string): Promise<ReitDeposit[]> {
    try {
      let query = supabase
          .from('reit_deposits')
          .select('*')
          .eq('user_id', userId)
          .order('deposit_date', { ascending: false });

      if (holdingId) query = query.eq('reit_holding_id', holdingId);
      const { data, error } = await query;

      if (error) throw error;
      return data;
    } catch (error) {
      console.error("Error getting REIT deposits:", error);
      await this.handleError(error);
    }
  }

  /**
   * Creates a new REIT deposit record in the system using the provided user ID and deposit details.
   *
   * @param {string} userId - The unique identifier of the user creating the deposit.
   * @param {CreateReitDividendDto} request - The details of the REIT deposit including properties such as
   * reit holding ID, source account ID, number of units purchased, price per unit, deposit currency, fee,
   * deposit date, and additional notes.
   * @return {Promise<ReitDeposit>} A Promise that resolves to the created REIT deposit record.
   * @throws {Error} If an error occurs during the deposit creation process.
   */
  public async createDeposit(userId: string, request: CreateReitDepositDto): Promise<ReitDeposit> {
    try {
      const payload = this.convertRequestToSnakeCase(request);
      const { convertedAmount, convertedFee } = await convertToAccountCurrency(
        userId,
        request.sourceAccountId,
        request.unitsPurchased * request.pricePerUnit,
        request.fee ?? 0,
        request.depositCurrency
      );
      const { data, error } = await supabase.rpc('create_reit_deposit_with_debit', {
        p_user_id: userId,
        p_reit_holding_id: payload.reit_holding_id,
        p_source_account_id: payload.source_account_id,
        p_units_purchased: payload.units_purchased,
        p_price_per_unit: payload.price_per_unit,
        p_deposit_currency: payload.deposit_currency,
        p_fee: payload.fee ?? 0,
        p_deposit_date: payload.deposit_date,
        p_notes: payload.notes ?? null,
        p_debit_amount: convertedAmount,
        p_debit_fee: convertedFee,
      });
      if (error) throw new Error(error.message || 'Failed to record REIT deposit');
      return data;
    } catch (error) {
      console.error("Error creating REIT deposit:", error);
      await this.handleError(error);
    }
  }

  /**
   * Records a dividend for a specific user by interacting with a remote procedure call (RPC).
   *
   * @param {string} userId - The unique identifier of the user for whom the dividend is being recorded.
   * @param {CreateReitDepositDto} request - The data transfer object containing dividend details, such as payment date, amount per unit, units held, currency, and additional optional parameters.
   * @return {Promise<ReitDeposit>} A promise that resolves to the recorded dividend data.
   */
  public async recordDividend(userId: string, request: RecordREITDividendDto): Promise<ReitDividendPayment> {
    try {
      const input = this.convertRequestToSnakeCase(request);
      const creditAmount = input.dividend_action === 'deposit_to_account' && input.target_account_id
        ? (await convertToAccountCurrency(
            userId,
            input.target_account_id,
            input.amount_per_unit * input.units_held_at_payment,
            0,
            input.currency
          )).convertedAmount
        : null;
      const { data, error } = await supabase.rpc('record_reit_dividend', {
        p_reit_holding_id: input.reit_holding_id,
        p_user_id: userId,
        p_payment_date: input.payment_date,
        p_amount_per_unit: input.amount_per_unit,
        p_units_held_at_payment: input.units_held_at_payment,
        p_currency: input.currency,
        p_notes: input.notes ?? null,
        p_dividend_action: input.dividend_action,
        p_target_account_id: input.target_account_id ?? null,
        p_reinvested_units: input.reinvested_units ?? null,
        p_credit_amount: creditAmount,
      });
      if (error) throw new Error(error.message || 'Failed to record dividend');
      return data;
    } catch (error) {
      console.error("Failed to record dividend", error);
      await this.handleError(error);
    }
  }

  /**
   * Deletes a REIT dividend payment. A payment deposited to an account is deleted together with its
   * transaction and the account's balance is restored; reinvested units are removed from the holding.
   *
   * @param {string} userId - The unique identifier of the user who owns the payment.
   * @param {string} paymentId - The unique identifier of the dividend payment.
   */
  public async deleteDividendPayment(userId: string, paymentId: string): Promise<void> {
    try {
      const { error } = await supabase.rpc('delete_reit_dividend_payment', {
        p_payment_id: paymentId,
        p_user_id: userId,
      });
      if (error) throw new Error(error.message || 'Failed to delete dividend payment');
    } catch (error) {
      console.error("Failed to delete dividend payment", error);
      await this.handleError(error);
    }
  }

  // Reit Metadata

  /**
   * Retrieves a list of REIT types from the database, including their ID, name, and description.
   * The list is sorted by name in ascending order.
   *
   * @return {Promise<ReitTypeResponse[]>} A promise that resolves to an array of REIT types, each containing an ID, name, and description.
   * @throws Will throw an error if the database query fails.
   */
  public async getTypes(): Promise<ReitTypeResponse[]> {
    try {
      const { data, error } = await supabase
          .from('reit_types')
          .select('id, name, description')
          .order('name', { ascending: true });
      if (error) throw error;
      return data;
    } catch (error) {
      console.error("Error getting REIT types:", error);
      await this.handleError(error);
    }
  }

  /**
   * Retrieves the list of REIT sectors from the database, including their ID, name, and description, sorted by name in ascending order.
   *
   * @return {Promise<ReitSectorResponse[]>} A promise that resolves to an array of objects representing REIT sectors.
   * Each object contains the ID, name, and description of a REIT sector. In case of an error, it logs the error and handles it through a designated error handling method.
   */
  public async getSectors(): Promise<ReitSectorResponse[]> {
    try {
      const { data, error } = await supabase
          .from('reit_sectors')
          .select('id, name, description')
          .order('name', { ascending: true });
      if (error) throw error;
      return data;
    } catch (error) {
      console.error("Error getting REIT deposits:", error);
      await this.handleError(error);
    }
  }
}

export default new ReitService();
